'use client'

import Link from 'next/link'
import { MC, SERIF } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOCopy, getMyCIOLandingCopy, getMyCIOYourDataCopy } from '@/lib/mycio/translations'
import { SUPPORT_EMAIL } from '@/lib/mycio/brand'
import type { DocType } from './content'

/**
 * The landing footer (design: `MyCIO Landing.dc.html` footer — #1A3A30, three link columns, disclosure line).
 * Shared by the landing, the country coverage pages and onboarding so the surfaces read as one product.
 * `onDoc` opens the legal modal in place (landing); without it the legal links deep-link to `/#terms` etc.,
 * which the landing turns into the same modal.
 */
export default function MarketingFooter({ onDoc }: { onDoc?: (doc: DocType) => void }) {
  const { language } = useMyCIOLanguage()
  const copy = getMyCIOCopy(language)
  const landing = getMyCIOLandingCopy(language)
  const yourData = getMyCIOYourDataCopy(language)
  const link: React.CSSProperties = { fontSize: 15, color: '#D8E2DB', textDecoration: 'none', cursor: 'pointer' }
  const head: React.CSSProperties = { fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6E8579', fontWeight: 700, margin: '0 0 3px' }
  const legal: [DocType, string, string][] = [
    ['terms', '/#terms', landing.footer.terms],
    ['privacy', '/#privacy', landing.footer.privacy],
    ['refund', '/#refunds', landing.footer.refund],
    ['disclosures', '/#disclosures', landing.footer.disclosures],
    ['risk', '/#risk', landing.footer.risk],
  ]
  return (
    <footer style={{ background: '#1A3A30', color: '#D8E2DB' }}>
      <div className="mc-container" style={{ maxWidth: 1180, margin: '0 auto', padding: '64px 32px 48px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 48, flexWrap: 'wrap', paddingBottom: 36, borderBottom: '1px solid rgba(216,226,219,0.18)' }}>
          <div style={{ maxWidth: 340 }}>
            <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500, color: MC.bg, marginBottom: 12 }}>MyCIO<span style={{ color: MC.sage }}>.ai</span></div>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: '#A9BDB2', margin: 0 }}>{landing.footer.blurb}</p>
          </div>
          <div style={{ display: 'flex', gap: 56, flexWrap: 'wrap' }}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              <p style={head}>{landing.footer.product}</p>
              <Link href="/#why" className="mc-foot-link" style={link}>{copy.nav.why}</Link>
              <Link href="/coverage" className="mc-foot-link" style={link}>{copy.nav.coverage}</Link>
              <Link href="/your-data" className="mc-foot-link" style={link}>{yourData.navLabel}</Link>
              <Link href="/#pricing" className="mc-foot-link" style={link}>{copy.nav.pricing}</Link>
              <Link href="/sign-in" className="mc-foot-link" style={link}>{copy.nav.signin}</Link>
            </nav>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 13, alignItems: 'flex-start' }}>
              <p style={head}>{landing.footer.legal}</p>
              {legal.map(([doc, href, label]) => (
                <Link key={doc} href={href} onClick={onDoc ? () => onDoc(doc) : undefined} className="mc-foot-link" style={link}>{label}</Link>
              ))}
            </nav>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              <p style={head}>{copy.common.contact}</p>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="mc-foot-link" style={link}>{SUPPORT_EMAIL}</a>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 12.5, lineHeight: 1.6, color: '#80978C', margin: '28px 0 0', maxWidth: 860 }}>
          {landing.footer.disclosure} © 2026 Parsenne.{' '}
          <Link href="/#disclosures" onClick={onDoc ? () => onDoc('disclosures') : undefined} style={{ fontSize: 12.5, color: MC.sage, cursor: 'pointer', textDecoration: 'underline' }}>{landing.footer.readDisclosures}</Link>.
        </p>
      </div>
    </footer>
  )
}
